import React, { useState, useMemo } from "react";
import { createOrUpdateRSVP } from "../../services/rsvp-service";
import { useAuth0 } from "@auth0/auth0-react";
import "../../styles/components/events.css";
import Loader from "../navigation/Loader";
import toast from 'react-hot-toast';
import RsvpCounts from "./rsvp-counts";


export default function AdminRSVPList({
  rsvpList = [],
  eventID,
  eventName,
  eventDate,
  isReadOnly = false,
  onlyYesNo = false,
  editableContactID = null,
}) {

  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);

  // Sort initially: Yes -> No -> null
  const sortedInitialList = useMemo(() => {
    return [...rsvpList].sort((a, b) => {
      const isCurrentUserA =
        editableContactID && String(a.contactID) === String(editableContactID);
      const isCurrentUserB =
        editableContactID && String(b.contactID) === String(editableContactID);

      // Keep the current user at the top when available.
      if (isCurrentUserA && !isCurrentUserB) return -1;
      if (!isCurrentUserA && isCurrentUserB) return 1;

      const statusOrder = (status) =>
        status?.toLowerCase() === "yes" ? 0 :
        status?.toLowerCase() === "no" ? 1 : 2;
      return statusOrder(a.status) - statusOrder(b.status);
    });
  }, [rsvpList, editableContactID]);

  const [list, setList] = useState(sortedInitialList);

  const displayedList = useMemo(() => {
    if (!onlyYesNo) return list;
    return list.filter((rsvp) => {
      const status = rsvp.status?.toLowerCase();
      const isCurrentUsersRsvp =
        isReadOnly &&
        editableContactID &&
        String(rsvp.contactID) === String(editableContactID);
      return status === "yes" || status === "no" || isCurrentUsersRsvp;
    });
  }, [list, onlyYesNo, isReadOnly, editableContactID]);

  const yesCount = useMemo(() => displayedList.filter(r => r.status?.toLowerCase() === "yes").length, [displayedList]);
  const noCount = useMemo(() => displayedList.filter(r => r.status?.toLowerCase() === "no").length, [displayedList]);

  async function toggleRSVP(rsvp, status) {
    const newStatus = status.toLowerCase();
    if (rsvp.status?.toLowerCase() === newStatus) return;

    try {
      setLoading(true);
      const accessToken = await getAccessTokenSilently();

      const formData = new FormData();
      if (rsvp.rsvpID) formData.append("rsvpID", rsvp.rsvpID);
      formData.append("eventID", rsvp.eventID || eventID);
      formData.append("contactID", rsvp.contactID);
      formData.append("status", newStatus);

      formData.append("phone", rsvp.phone || "");
      formData.append("email", rsvp.email || "");
      formData.append("language", rsvp.language || "en");
      formData.append("eventName", eventName || "");
      formData.append("eventDate", eventDate || "");

      const { error } = await createOrUpdateRSVP(accessToken, formData, rsvp.rsvpID || null);

      if (!error) {
        toast.success(`RSVP Updated!`);
        setList((prev) =>
          prev.map((item) => {
            const isMatch = rsvp.rsvpID
              ? item.rsvpID === rsvp.rsvpID
              : item.contactID === rsvp.contactID &&
                (item.eventID || eventID) === (rsvp.eventID || eventID);

            return isMatch ? { ...item, status: newStatus } : item;
          })
        );
      } else {
        toast.error("there was an issue!");
      }
    } catch (err) {
      console.error("Token or update error:", err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="admin-rsvp-container">
      <RsvpCounts yesCount={yesCount} noCount={noCount} />
      <div className="admin-rsvp-list">
        {displayedList.map((rsvp) => {
          const currentStatus = rsvp.status?.toLowerCase();
          const rowIsEditable =
            !isReadOnly ||
            (editableContactID && String(rsvp.contactID) === String(editableContactID));

          return (
            <div
              key={rsvp.rsvpID || `${rsvp.contactID}-${rsvp.eventID}`}
              className="admin-rsvp-row"
            >
              <span className="admin-rsvp-name">
                {rsvp.firstName} {rsvp.lastName}
              </span>

              {rowIsEditable && (
                <div className="admin-rsvp-actions">
                  <button
                    className={`rsvp-status-btn yes ${
                      currentStatus === "yes" ? "" : "grayed"
                    }`}
                    onClick={() => toggleRSVP(rsvp, "yes")}
                    title="Set RSVP to Yes"
                  >
                    {loading ? <Loader /> :
                                  <>
                    <span className="emoji">✅</span> Yes
                    </>
                  }
                  </button>
                  <button
                    className={`rsvp-status-btn no ${
                      currentStatus === "no" ? "" : "grayed"
                    }`}
                    onClick={() => toggleRSVP(rsvp, "no")}
                    title="Set RSVP to No"
                  >
                    {loading ? <Loader /> :
                                  <>
                    <span className="emoji">❌</span> No
                    </>
                  }
                  </button>
                </div>
              )}

              {!rowIsEditable && (
                <span className={`rsvp-count ${currentStatus === "yes" ? "yes" : currentStatus === "no" ? "no" : ""}`}>
                  {currentStatus === "yes" ? "✅ Yes" : currentStatus === "no" ? "❌ No" : "No RSVP"}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}